import React, { useState, useCallback } from 'react';
import { Play, ExternalLink, ChevronLeft, ChevronRight, Maximize2, X, Info } from 'lucide-react';
import SectionWrapper from './SectionWrapper';

const DemoSection = ({ section, index, sectionLayout, showSectionIcons, showSectionNumbers, enableSectionCollapse, layout }) => {
  const [selectedDemo, setSelectedDemo] = useState(section.demos?.[0] || null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [overlayDismissed, setOverlayDismissed] = useState(false); // Moved to component level

  const getInteractiveHeight = (size) => {
    switch (size) {
      case 'medium': return 'h-[500px]';
      case 'large': return 'h-[600px]';
      case 'extra-large': return 'h-[700px]';
      case 'full-height': return 'h-[80vh]';
      default: return 'h-[600px]';
    }
  };

  const getEmbedUrl = useCallback((url, platform) => {
    if (!url) return null;
    
    const extractVideoId = (url, platform) => {
      switch (platform) {
        case 'youtube':
          const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
          return youtubeMatch ? youtubeMatch[1] : null;
        case 'vimeo':
          const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
          return vimeoMatch ? vimeoMatch[1] : null;
        case 'loom':
          const loomMatch = url.match(/(?:loom\.com\/share\/)([a-f0-9]+)/);
          return loomMatch ? loomMatch[1] : null;
        default:
          return null;
      }
    };

    const videoId = extractVideoId(url, platform);
    if (!videoId) return null;

    switch (platform) {
      case 'youtube':
        return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
      case 'vimeo':
        return `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`;
      case 'loom':
        return `https://www.loom.com/embed/${videoId}?hide_owner=true&hide_share=true&hide_title=true`;
      default:
        return null;
    }
  }, []);

  const openImageModal = (image, index) => {
    setModalImage({ ...image, index });
    setShowImageModal(true);
  };

  const nextImage = () => {
    if (selectedDemo.images && modalImage) {
      const nextIndex = (modalImage.index + 1) % selectedDemo.images.length;
      setModalImage({ ...selectedDemo.images[nextIndex], index: nextIndex });
    }
  };

  const prevImage = () => {
    if (selectedDemo.images && modalImage) {
      const prevIndex = (modalImage.index - 1 + selectedDemo.images.length) % selectedDemo.images.length;
      setModalImage({ ...selectedDemo.images[prevIndex], index: prevIndex });
    }
  };

  const renderDemoContent = () => {
    if (!selectedDemo) return null;

    switch (selectedDemo.type) {
      case 'interactive':
        return (
          <div className="space-y-4">
            {/* Toggleable info bar */}
            {!overlayDismissed && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-blue-900 text-lg font-semibold mb-1">{selectedDemo.title}</h3>
                    <p className="text-blue-700 text-sm">{selectedDemo.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <a
                      href={selectedDemo.demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Open
                    </a>
                    
                    <button
                      onClick={() => setOverlayDismissed(true)}
                      className="p-1 text-blue-400 hover:text-blue-600 rounded"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Clean iframe */}
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={selectedDemo.demoUrl}
                className={`w-full ${getInteractiveHeight(selectedDemo.displaySize)}`}
                frameBorder="0"
                allowFullScreen={selectedDemo.embedSettings?.allowFullscreen !== false}
                title={selectedDemo.title}
                loading="lazy"
              />
            </div>

            {/* Show info button if dismissed */}
            {overlayDismissed && (
              <button
                onClick={() => setOverlayDismissed(false)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <Info className="h-4 w-4" />
                Show demo info
              </button>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
            <div className="relative aspect-video">
              {selectedDemo.videoPlatform === 'upload' ? (
                <video
                  src={selectedDemo.url}
                  poster={selectedDemo.thumbnail}
                  controls
                  className="w-full h-full object-cover"
                />
              ) : (
                <iframe
                  src={getEmbedUrl(selectedDemo.videoUrl, selectedDemo.videoPlatform)}
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  title={selectedDemo.title}
                  loading="lazy"
                />
              )}
              
              {/* Overlay is OK for videos since they're not interactive */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <h3 className="text-white text-xl font-semibold mb-2">{selectedDemo.title}</h3>
                <p className="text-gray-200 text-sm line-clamp-2">{selectedDemo.description}</p>
              </div>
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-6">
            {selectedDemo.images && selectedDemo.images.length > 0 && (
              <>
                {/* Main Image Display */}
                <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-lg">
                  <div className="relative aspect-video group cursor-pointer">
                    <img
                      src={selectedDemo.images[currentImageIndex]?.url}
                      alt={selectedDemo.images[currentImageIndex]?.description || `Screenshot ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                      onClick={() => openImageModal(selectedDemo.images[currentImageIndex], currentImageIndex)}
                    />
                    
                    {/* Navigation and expand controls */}
                    {selectedDemo.images.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(prev => 
                              prev === 0 ? selectedDemo.images.length - 1 : prev - 1
                            );
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(prev => 
                              prev === selectedDemo.images.length - 1 ? 0 : prev + 1
                            );
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => openImageModal(selectedDemo.images[currentImageIndex], currentImageIndex)}
                      className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                    
                    {/* Image info overlay is OK for images */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                      <h3 className="text-white text-xl font-semibold mb-2">{selectedDemo.title}</h3>
                      {selectedDemo.images[currentImageIndex]?.description && (
                        <p className="text-gray-200 text-sm line-clamp-2">
                          {selectedDemo.images[currentImageIndex].description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-gray-300 text-xs">
                          {currentImageIndex + 1} of {selectedDemo.images.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Thumbnail navigation */}
                {selectedDemo.images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {selectedDemo.images.map((image, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          currentImageIndex === idx 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img 
                          src={image.url}
                          alt={image.description || `Screenshot ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Reset overlay dismissed state when demo changes
  React.useEffect(() => {
    setOverlayDismissed(false);
  }, [selectedDemo?.id]);

  if (!section.demos || section.demos.length === 0) {
    return null;
  }

  return (
    <SectionWrapper
      title={section.title || "Product Demo"}
      index={index}
      sectionLayout={sectionLayout}
      showSectionIcons={showSectionIcons}
      showSectionNumbers={showSectionNumbers}
      enableSectionCollapse={enableSectionCollapse}
      icon={<Play className="h-5 w-5 text-blue-600" />}
      layout={layout}
    >
      <div className="space-y-8">
        {/* Main Demo Content */}
        <div className="space-y-6">
          {renderDemoContent()}
        </div>

        {/* Demo Selector Tabs */}
        {section.demos && section.demos.length > 1 && (
          <div className="border-t pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {section.demos.map((demo, idx) => (
                <button
                  key={demo.id || idx}
                  onClick={() => {
                    setSelectedDemo(demo);
                    setCurrentImageIndex(0);
                  }}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    selectedDemo?.id === demo.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      demo.type === 'interactive' ? 'bg-blue-100 text-blue-600' :
                      demo.type === 'video' ? 'bg-purple-100 text-purple-600' :
                      'bg-green-100 text-green-600'
                    }`}>
                      {demo.type === 'interactive' ? <ExternalLink className="h-4 w-4" /> :
                       demo.type === 'video' ? <Play className="h-4 w-4" /> :
                       <img className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{demo.title}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{demo.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          demo.type === 'interactive' ? 'bg-blue-100 text-blue-700' :
                          demo.type === 'video' ? 'bg-purple-100 text-purple-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {demo.type === 'interactive' ? 'Interactive' :
                           demo.type === 'video' ? 'Video' : 'Gallery'}
                        </span>
                        {demo.type === 'gallery' && demo.images && (
                          <span className="text-xs text-gray-500">
                            {demo.images.length} images
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && modalImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
            >
              <X className="h-6 w-6" />
            </button>
            
            {selectedDemo.images && selectedDemo.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
            
            <div className="relative max-w-full max-h-full">
              <img
                src={modalImage.url}
                alt={modalImage.description || 'Screenshot'}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              
              {modalImage.description && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white text-center">{modalImage.description}</p>
                  {selectedDemo.images && selectedDemo.images.length > 1 && (
                    <p className="text-gray-300 text-center text-sm mt-2">
                      {modalImage.index + 1} of {selectedDemo.images.length}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </SectionWrapper>
  );
};

export default DemoSection;